import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * NavBarItem - Premium animated navigation icon
 *
 * Features:
 * - Tap animation: scale down 0.95 → spring back
 * - Active state: scale 1.1 + color change + underline indicator
 * - Badge: animated pop-in with count
 * - 44px minimum touch target
 * - Haptic-style visual feedback
 *
 * @param {Component} icon - Lucide icon component
 * @param {string} label - Text label below icon
 * @param {boolean} isActive - Whether this tab is active
 * @param {number} badge - Badge count (0 = hidden)
 * @param {function} onTap - Callback when tapped
 */
export default function NavBarItem({
  icon: Icon,
  label,
  isActive = false,
  badge = 0,
  onTap
}) {
  const [isPressed, setIsPressed] = useState(false);

  const handleTouchStart = () => {
    setIsPressed(true);
  };

  const handleTouchEnd = () => {
    setIsPressed(false);
  };

  const handleClick = () => {
    onTap();
  };

  // Calculate scale based on pressed and active states
  const scale = isPressed ? 0.95 : (isActive ? 1.1 : 1.0);

  return (
    <motion.button
      className="flex flex-col items-center justify-center relative min-w-[44px] min-h-[44px] px-2 py-1 select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      animate={{ scale }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }}
    >
      {/* Icon with color animation */}
      <motion.div
        className="relative"
        animate={{
          color: isActive ? '#60a5fa' : '#9CA3AF'
        }}
        transition={{ duration: 0.2 }}
      >
        <Icon size={24} strokeWidth={2} />

        {/* Badge */}
        <AnimatePresence>
          {badge > 0 && (
            <motion.div
              className="absolute -top-1 -right-1 bg-gjyl-danger rounded-full min-w-[16px] h-4 flex items-center justify-center px-1"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.2, 1], opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeOut'
              }}
            >
              <span className="text-[10px] font-bold text-white leading-none">
                {badge > 99 ? '99+' : badge}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Label */}
      <motion.span
        className="text-xs mt-1 font-medium"
        animate={{
          color: isActive ? '#60a5fa' : '#9CA3AF'
        }}
        transition={{ duration: 0.2 }}
      >
        {label}
      </motion.span>

      {/* Active indicator (underline) */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            className="absolute -bottom-0.5 w-5 h-0.5 bg-gjyl-accent rounded-full"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: 'easeOut'
            }}
          />
        )}
      </AnimatePresence>
    </motion.button>
  );
}
